// /tickets/canvas — 3-pane board: left epic nav, center grouped board,
// right filters + selected-ticket detail.
//
// Replaces the old cytoscape graph with a real ticket board that reuses
// EpicsView/StoriesSection/StoryCard component patterns and the existing
// TicketCard visual language from /tickets/general and /tickets/my.
//
// URL params: ?groupBy=story|epic|status|priority|assignee|none
//             ?epic=<epicKey>
//
// Drag-to-bucket: pointer event delegation at board level. Limit
// pointermove updates via rAF. No per-card signal listeners. (see #4382)

import { pageBoilerplateFetch } from '../../functions/tickets/ticketUtils.js'
import { fetchCanvasTriage } from '../../functions/tickets/fetchCanvasTriage.js'
import { subscribeTicketsRealtime } from '../../functions/tickets/subscribeTicketsRealtime.js'
import { SUPABASE_URL, restHeaders } from '../../../shared/functions/supabaseConfig.js'

const _VALID_GROUP_BY = ['story', 'epic', 'status', 'priority', 'assignee', 'none']

// Resolve the ?ticket=<id> URL param after the canvas data is ready.
// 1. Immediately set a partial selectedTicket from allTickets (fast path —
//    shows title/state/priority while the full fetch is in-flight).
// 2. Always fetch select=* to hydrate body + all fields.
// This runs AFTER fetchCanvasTriage() resolves so canvasAllTickets is
// guaranteed to be populated, avoiding the 5-second polling race in
// TicketsDetailModal.onRender (root cause of #4445 / #4417 follow-up).
const _restoreTicketFromUrl = async (root, allTickets) => {
  if (typeof window === 'undefined') return
  let urlId
  try { urlId = new URL(window.location).searchParams.get('ticket') } catch { return }
  if (!urlId) return
  // Skip only if body is already populated — a plain {id} stub from the
  // modal's onRender polling should NOT block the select=* fetch.
  if (root?.selectedTicket?.body !== undefined) return

  const numId = parseInt(urlId, 10)
  const matchId = !isNaN(numId) ? numId : null

  // Fast-path: find partial ticket from already-loaded allTickets array.
  const partial = (allTickets || []).find(
    t => (matchId && t.id === matchId) || String(t.external_id) === urlId
  )
  if (partial) {
    // Set immediately so the detail pane shows title/state while hydrating.
    root.update({ selectedTicket: { ...partial } })
  }

  // Always fetch the full row so body + all fields are present.
  try {
    const fetchIdParam = matchId ? `id=eq.${matchId}` : `external_id=eq.${encodeURIComponent(urlId)}`
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/tickets?${fetchIdParam}&select=*&limit=1`,
      { headers: restHeaders() }
    )
    if (!res.ok) return
    const rows = await res.json()
    const t = rows[0]
    if (!t) return
    root.update({ selectedTicket: {
      ...(root.selectedTicket || {}),
      ...(partial || {}),
      ...t,
      columnKey: t.column_key,
      issueType: t.issue_type,
      epicName: t.epic_name,
      assigneeEmail: t.assignee_email,
      reporterEmail: t.reporter_email,
      updatedAt: t.updated_at,
      createdAt: t.created_at
    } })
  } catch (_) {}
}

const _readUrlParams = () => {
  if (typeof window === 'undefined') return {}
  try {
    const p = new URLSearchParams(window.location.search)
    const groupBy = p.get('groupBy')
    const epic = p.get('epic')
    return {
      canvasGroupBy: _VALID_GROUP_BY.includes(groupBy) ? groupBy : 'story',
      canvasEpicFilter: epic || null
    }
  } catch { return {} }
}

export const canvas = {
  extends: 'AppPage',
  metadata: { title: 'Canvas / Tickets / Symbols Workspace' },

  fetch: pageBoilerplateFetch,

  onCreate: async (el, s) => {
    // Initialise URL-driven state before data lands.
    const urlState = _readUrlParams()
    s.root.update({
      canvasGroupBy: urlState.canvasGroupBy || 'story',
      canvasEpicFilter: urlState.canvasEpicFilter || null,
      canvasStateFilter: null,
      canvasPriorityFilter: null,
      canvasSelected: [],
      canvasAllTickets: null,  // null = loading
      canvasEpics: [],
      canvasStories: []
    })

    const { epics, stories, allTickets } = await fetchCanvasTriage()
    s.root.update({
      canvasAllTickets: allTickets,
      canvasEpics: epics,
      canvasStories: stories
    })

    // Resolve ?ticket=<id> AFTER canvas data is ready — bypasses the
    // TicketsDetailModal 5s polling race (fixes #4445 / #4417 follow-up).
    await _restoreTicketFromUrl(s.root, allTickets)

    // Keyboard shortcut handler: g+e = epics, g+s = stories, g+c = canvas
    let _gPending = false
    const _keyHandler = (e) => {
      if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return
      if (e.key === 'g') { _gPending = true; setTimeout(() => { _gPending = false }, 500); return }
      if (_gPending) {
        _gPending = false
        if (e.key === 'e') { window.location.href = '/tickets/epics'; return }
        if (e.key === 's') { window.location.href = '/tickets/stories'; return }
        if (e.key === 'c') { window.location.href = '/tickets/canvas'; return }
      }
      // Arrow key navigation between cards: handled natively via tabindex.
    }
    document.addEventListener('keydown', _keyHandler)
    el._keyHandlerCleanup = () => document.removeEventListener('keydown', _keyHandler)

    // Realtime refresh
    el._rtUnsub = subscribeTicketsRealtime({
      pageName: 'canvas',
      tables: ['tickets'],
      onRefresh: async () => {
        const { epics: e2, stories: s2, allTickets: t2 } = await fetchCanvasTriage()
        s.root.update({ canvasAllTickets: t2, canvasEpics: e2, canvasStories: s2 })
      }
    })
  },

  onRemove: (el) => {
    if (el._rtUnsub) { el._rtUnsub(); el._rtUnsub = null }
    if (el._keyHandlerCleanup) { el._keyHandlerCleanup(); el._keyHandlerCleanup = null }
    const root = el.getRoot()
    if (root && root.selectedTicket) root.update({ selectedTicket: null })
  },

  TicketsSidebar: {},
  TicketsCanvasMain: {},
  TicketsNewTicketModal: {},
  TicketsDetailModal: {}
}
